import { makeAutoObservable } from 'mobx';
import { AddAccountButtonViewModelInterface } from '@shared/types/mobx/features/osiAccounts';
import { DictionaryModule } from '@mobx/services/dictionaries/model/DictionaryModule';
import { AccountType, Bank } from '@shared/types/dictionaries';
import { AccountForm, OsiAccountTypes } from '@shared/types/osi/accounts';
import { getErrorText, notifications } from '@shared/utils/notifications';
import { inject, injectable } from 'inversify';
import { ApplicationTypes, OsiAccountApplicationCheckRequest } from '@shared/types/osiAccountApplications';
import { checkIsHasActiveOsiAccountApplication } from '@shared/api/osiAccountApplications';
import logger from 'js-logger';
import { OsiAccount } from '@shared/types/osi';
import { type OsiService, OsiServiceIdentifier } from '@shared/types/mobx/services/osi';

@injectable()
export class AddAccountButtonFeature implements AddAccountButtonViewModelInterface {
  isFeatureDisabled = false;

  isDialogOpen = false;

  hookForm: any = null;

  defaultValues: AccountForm = {
    account: '',
    accountType: '',
    bank: {
      bic: '',
      name: ''
    }
  };

  accounts: OsiAccount[] = [];

  constructor(private dictionary: DictionaryModule, @inject(OsiServiceIdentifier) private osiModule: OsiService) {
    makeAutoObservable(this);
  }

  get accountTypes(): AccountType[] {
    return this.dictionary.accountTypes;
  }

  get banks(): Bank[] {
    return this.dictionary.banks;
  }

  get isCurrentAccountExists() {
    return this.accounts?.some((account) => account?.type === OsiAccountTypes.CURRENT) ?? false;
  }

  setFeatureStatus = (value: boolean) => {
    this.isFeatureDisabled = value;
  };

  addAccountCb: (data: AccountForm) => void = () => {};

  setForm = (hookForm: any): void => {
    this.hookForm = hookForm;
  };

  onChangeAccount = (value: string): void => {
    // OSI-806: единая нормализация — в форму (а значит и в валидатор checkIban, и на бэк)
    // уходит uppercase без пробелов. Валидатор строгий и сам ничего не нормализует.
    const normalized = value.toUpperCase().replace(/\s/g, '');
    this.hookForm.setValue('account', normalized);
    this.hookForm.trigger('account');
    if (normalized.length < 7) return;

    const bicId = normalized.slice(4, 7);
    const bank = this.banks.find((bank) => bank.identifier === bicId);

    if (!bank) return;

    this.hookForm.setValue('bank', {
      bic: bank.bic,
      name: bank.name
    });
    this.hookForm.trigger();
  };

  // Фаза 0: проверяем наличие обоих базовых счетов
  get isSavingsAccountExists() {
    return this.accounts?.some((account) => account?.type === OsiAccountTypes.SAVINGS) ?? false;
  }

  get isBaseAccountsFilled() {
    return this.isCurrentAccountExists && this.isSavingsAccountExists;
  }

  onAddAccountClick = () => {
    if (this.isFeatureDisabled) {
      notifications.warning('Для добавления счета обратитесь к оператору');
      return;
    }

    this.isDialogOpen = true;

    // Определяем тип счёта для автоподстановки: если уже есть CURRENT — предвыбираем SAVINGS
    let accountType = '';
    if (this.isCurrentAccountExists && !this.isSavingsAccountExists) {
      accountType = OsiAccountTypes.SAVINGS;
    }

    // Устанавливаем accountType внутри reset, чтобы form.watch() сразу получил значение
    this.hookForm.reset({ ...this.defaultValues, accountType });
  };

  onCloseDialogClick = () => {
    this.isDialogOpen = false;
    this.hookForm.reset(this.defaultValues);
    this.hookForm.trigger();
  };

  // OSI-806: раньше диалог закрывался безусловно — и когда форма невалидна
  // (handleSubmit вообще не вызывал saveAccount), и когда сохранение упало.
  // Пользователь терял введённые данные и не мог исправить счёт по тексту ошибки.
  // Теперь закрытие живёт только в успешной ветке saveAccount.
  onSaveAccountClick = async () => {
    await this.hookForm.handleSubmit(this.saveAccount)();
  };

  saveAccount = async (data: AccountForm) => {
    try {
      await this.checkAccountApplications(data.accountType);

      await this.addAccountCb(data);

      this.onCloseDialogClick();
    } catch (e) {
      logger.error(e);
      // OSI-806: в notifications.error уходит СТРОКА (текст ошибки бэка), а не объект Error —
      // i18next.t(Error) показывал пользователю мусор вместо «Указан неверный счет».
      notifications.error(getErrorText(e));
    }
  };

  checkAccountApplications = async (accountType: string) => {
    if (!this.osiModule.osiId) return false;

    const payload: OsiAccountApplicationCheckRequest = {
      osiId: this.osiModule.osiId,
      type: accountType as OsiAccountTypes,
      applicationType: ApplicationTypes.ADD
    };

    await checkIsHasActiveOsiAccountApplication(payload);
  };

  setAddAccountCb(cb: (data: AccountForm) => void): void {
    this.addAccountCb = cb;
  }
}
